import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSubscriptionService } from 'hooks/sales/subscription';

const TIER_UPGRADE_MONTHS = 3;

const monthsBetween = (start, end) => {
  if (!start || !end) return 0;
  const from = new Date(start);
  const to = new Date(end);
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  return Math.max(months, 0);
};

const RENEWAL_PERIOD_MONTHS = { month: 1, year: 12 };

// Prior (non-current) subscriptions have already run their course, so their
// nominal renewal period is a reliable stand-in for time spent on them. The
// current, still-open subscription hasn't necessarily reached its expires_at
// yet, so its contribution is measured off its purchase-date monthniversary
// instead - how many monthly anniversaries of purchased_at have actually
// passed - rather than crediting it with a full period on day one.
const monthsOnPlan = (subscriptions, current) =>
  subscriptions
    .filter((sub) => sub.id !== current.id)
    .reduce((total, sub) => total + (RENEWAL_PERIOD_MONTHS[sub.renewal_period] || 1), 0) +
  monthsBetween(current.purchased_at, Date.now());

const determineUpsell = (current, subscriptions) => {
  if (!current) return null;
  const { tier } = current;
  const same_tier_subs = subscriptions.filter((sub) => sub.tier === tier);
  const tier_months = monthsOnPlan(same_tier_subs, current);

  if (tier === 'explorer' && tier_months >= TIER_UPGRADE_MONTHS) {
    return { to: 'professional' };
  }

  if (tier === 'professional' && tier_months >= TIER_UPGRADE_MONTHS) {
    return { to: 'enterprise' };
  }

  return null;
};

export const UpsellContext = createContext({
  upsell: null,
  loading: true,
  refresh: () => {}
});

export const UpsellProvider = ({ children }) => {
  const { tenant_id } = useSelector((store) => store.user_data);
  const { fetchSubscription } = useSubscriptionService();

  const [upsell, setUpsell] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!tenant_id) return;
    setLoading(true);
    fetchSubscription({ query_string: 'plan_code=campaign' })
      .then(({ subscriptions = [] }) => {
        const current = subscriptions.find((sub) => sub.status === 'active');
        setUpsell(determineUpsell(current, subscriptions));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <UpsellContext.Provider value={{ upsell, loading, refresh }}>{children}</UpsellContext.Provider>
  );
};
